
import { useMemo } from 'react';
import { 
  useFetchAppIdeas, 
  useFetchAppIdeaFavorites, 
  useToggleAppIdeaFavorite 
} from './use-fetch-app-ideas';
export type { AppIdea, AppIdeaFavorite } from './database-app-ideas';

/**
 * Main hook for app idea favorites functionality
 * Combines data fetching hooks and provides utility functions
 */
export const useAppIdeaFavorites = () => {
  const { data: appIdeas = [], isLoading: appIdeasLoading, error: appIdeasError } = useFetchAppIdeas();
  const { data: userFavorites = [], isLoading: favoritesLoading, error: favoritesError } = useFetchAppIdeaFavorites();
  const toggleFavoriteMutation = useToggleAppIdeaFavorite();
  
  // Memoize the isAppIdeaFavorited function to prevent unnecessary re-renders
  const isAppIdeaFavorited = useMemo(() => {
    return (appIdeaId: string) => {
      return userFavorites.some(fav => fav.app_idea_id === appIdeaId);
    };
  }, [userFavorites]);
  
  // Memoize the getAppIdeaFavoriteCount function to prevent unnecessary re-renders
  const getAppIdeaFavoriteCount = useMemo(() => {
    return (appIdeaId: string) => {
      // Find the app idea and return its favorite_count
      const appIdea = appIdeas.find(idea => idea.id === appIdeaId);
      return appIdea?.favorite_count || 0;
    };
  }, [appIdeas]);
  
  // Memoize the handleToggleFavorite function to prevent unnecessary re-renders
  const handleToggleFavorite = useMemo(() => {
    return async (appIdeaId: string) => {
      try {
        const isFavorited = isAppIdeaFavorited(appIdeaId);
        await toggleFavoriteMutation.mutateAsync({ appIdeaId, isFavorited });
      } catch (error) {
        // Error handling is done in the mutation
        console.error('Error toggling app idea favorite:', error);
      }
    };
  }, [isAppIdeaFavorited, toggleFavoriteMutation]);
  
  // Combine all loading states
  const isLoading = appIdeasLoading || favoritesLoading;
  const hasError = appIdeasError || favoritesError;
  
  // Memoize the enhanced app ideas with favorited status
  const enhancedAppIdeas = useMemo(() => {
    return appIdeas.map(appIdea => ({
      ...appIdea,
      is_favorited: isAppIdeaFavorited(appIdea.id),
      onToggleFavorite: () => handleToggleFavorite(appIdea.id)
    }));
  }, [appIdeas, isAppIdeaFavorited, handleToggleFavorite]);
  
  return {
    appIdeas: enhancedAppIdeas,
    userFavorites,
    favoriteCounts: [], // No longer used
    isAppIdeaFavorited,
    getAppIdeaFavoriteCount,
    handleToggleFavorite,
    isLoading,
    isMutating: toggleFavoriteMutation.isPending,
    hasError,
  };
};
